/**
 * Invoice API Service
 *
 * API calls for invoice and RFQ operations
 */

import { apiService, ApiResponse } from './api';

// =============================================================================
// Types
// =============================================================================

export interface RFQItem {
    id: string;
    company_request_id: number;
    product_name: string;
    product_description?: string;
    quantity: number;
    unit: string;
    budget?: number;
    currency?: string;
    total_savings?: number;
    savings_currency?: string;
    selected_supplier_name?: string;
    status: string;
    phase: string;
    completed_at?: string;
    created_at: string;
    updated_at: string;
    offer_count: number;
    has_invoice: boolean;
}

export interface OfferItem {
    id: string;
    supplier_name: string;
    unit_price: number;
    total_price: number;
    currency: string;
    lead_time?: string;
    moq?: number;
    status: string;
}

export interface RequestProduct {
    id: string;
    product_name: string;
    quantity: number;
    unit: string;
    budget?: number;
    product_description?: string;
}

export interface PhaseHistory {
    phase: string;
    timestamp: string;
    trigger: string;
    notes?: string;
    completed_by?: string;
    total_savings?: number;
}

export interface RFQDetail {
    rfq: RFQItem;
    request_products: RequestProduct[];
    offers: OfferItem[];
    phase_history: PhaseHistory[];
    selected_supplier_info?: {
        id?: string;
        name: string;
        region?: string;
        country?: string;
    };
}

export interface PaginatedResponse<T> {
    total: number;
    page: number;
    page_size: number;
    has_next: boolean;
    has_prev: boolean;
}

export interface RFQListResponse extends PaginatedResponse<RFQItem> {
    rfqs: RFQItem[];
}

export interface InvoiceItem {
    id: string;
    rfq_id: string;
    rfq_company_request_id: number;
    company_id: string;
    user_id: string;
    file_name: string;
    file_url: string;
    uploaded_at: string;
    status: string;
    notes?: string;
    created_at: string;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get completed RFQs with pagination
 */
export const getCompletedRFQs = async (
    page: number = 1,
    pageSize: number = 20
): Promise<ApiResponse<RFQListResponse>> => {
    return apiService.get<RFQListResponse>('/api/invoices/completed-rfqs', {
        page,
        page_size: pageSize
    });
};

/**
 * Get RFQ details by ID
 */
export const getRFQDetail = async (
    rfqId: string
): Promise<ApiResponse<RFQDetail>> => {
    return apiService.get<RFQDetail>(`/api/invoices/completed-rfqs/${rfqId}`);
};

/**
 * Upload invoice for RFQ
 */
export const uploadInvoice = async (
    rfqId: string,
    file: File,
    notes?: string
): Promise<ApiResponse<InvoiceItem>> => {
    return apiService.upload<InvoiceItem>(
        `/api/invoices/${rfqId}/upload`,
        file,
        notes ? { notes } : undefined
    );
};

/**
 * Get invoice by ID
 */
export const getInvoice = async (
    invoiceId: string
): Promise<ApiResponse<InvoiceItem>> => {
    return apiService.get<InvoiceItem>(`/api/invoices/invoice/${invoiceId}`);
};

/**
 * Delete invoice
 */
export const deleteInvoice = async (
    invoiceId: string
): Promise<ApiResponse<void>> => {
    return apiService.delete<void>(`/api/invoices/invoice/${invoiceId}`);
};

/**
 * Get invoices for RFQ
 */
export const getRFQInvoices = async (
    rfqId: string
): Promise<ApiResponse<{ invoices: InvoiceItem[]; total: number }>> => {
    return apiService.get<{ invoices: InvoiceItem[]; total: number }>(
        `/api/invoices/rfq/${rfqId}/invoices`
    );
};

// Export as object
const invoiceApi = {
    getCompletedRFQs,
    getRFQDetail,
    uploadInvoice,
    getInvoice,
    deleteInvoice,
    getRFQInvoices
};

export default invoiceApi;
